import React from 'react';
import { useCallback, useContext, useRef } from 'react';
import { CONVERSATION_CONSTANTS } from '../../config/miaw/constants';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import {
  IData,
  IMessageEvent,
  IParticipantChangedPayload,
  IRoutingResultPayload,
  IStaticMessageConversationPayload,
  ITypingIndicatorPayload,
} from '../../interfaces/agent-action-interfaces';
import { closeEventSource, subscribeToEventSource } from '../../utils/miaw/event-source-utils';
import { getConversationEventMessage } from '../../utils/component-utils';
import { CAContext } from '../../contexts/CAContext';
import { useNRLogging } from '../custom-hooks/useNRLogging';
import { handleLink } from '../../utils/miaw/agent-messages-utils';
import { setIsSendRoutingAttributes } from '../../utils/session-storage-utils';
import { ChatbotContext } from '../../contexts/ChatbotContext';
import ResumeButton from '../../components/AdvanceMessagingComponent/ResumeButton/ResumeButton';

export const useMIAWAgentActions = (): {
  subscribeToAgentActions: () => void;
  handleCloseEventSource: () => void;
} => {
  const { logCustomAttributesNR } = useNRLogging();
  const prevUnreadMessagesRef = useRef<number>(0);

  const {
    state: { enableSneakPeak },
    setState,
  } = useContext(MiawChatContext);
  const {
    setChatState,
    chatState: { chatWindow, playSoundMessageNotif },
  } = useContext(CAContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);

  const addEventListeners = useCallback(() => {
    subscribeToEventSource({
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_MESSAGE]: handleMessageEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_ROUTING_RESULT]: handleRoutingEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_PARTICIPANT_CHANGED]: handleParticipantChangedEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STARTED_INDICATOR]: handleTypingIndicatorEvent,
      [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_TYPING_STOPPED_INDICATOR]: handleTypingIndicatorEvent,
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_DELIVERY_ACKNOWLEDGEMENT]: (event) => { console.log('ACK EVENT', { event }); },
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_READ_ACKNOWLEDGEMENT]: (event) => { console.log('READ EVENT', { event }); },
      // [CONVERSATION_CONSTANTS.EventTypes.CONVERSATION_CLOSE_CONVERSATION]: (event) => { console.log('CLOSE EVENT', { event }); },
    });
  }, []);

  const handleCloseEventSource = useCallback(() => {
    closeEventSource();
  }, []);

  const handleResumeCTA = () => {
    setState((prev) => ({
      ...prev,
      conversationMessages: prev.conversationMessages.filter((m) => {
        const isResumeButton = typeof m.messageContent === 'object' && m.messageContent !== null && 'props' in m.messageContent && m.type === 'Resume';
        return !isResumeButton;
      }),
      isChatEndedByAgent: false,
    }));
  };

  const handleMessageEvent = useCallback(
    (event: IMessageEvent) => {
      const { data } = event;
      const parsedData = JSON.parse(data) as IData;
      const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload) as IStaticMessageConversationPayload;
      const staticContent = parsedPayload?.abstractMessage?.staticContent;
      let urlMapping: { label: string; url: string } | undefined = undefined;
      if (parsedPayload.abstractMessage.staticContent.formatType === 'RichLink') {
        const linkData = handleLink(parsedPayload);
        if (linkData) urlMapping = linkData;
      }
      const messageList: string[] = [];
      if (staticContent.text) {
        messageList.push(staticContent.text);
      }
      if (urlMapping?.url) {
        messageList.push(urlMapping.url);
      }
      /** Remove typing indicator on message receive */
      if (parsedData.conversationEntry.sender.role !== 'EndUser') {
        setState((prev) => ({ ...prev, conversationMessages: prev.conversationMessages.filter((message) => message.type !== 'Typing') }));
      }

      setState((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          {
            id: parsedData.conversationEntry.identifier,
            messageContent: messageList.length > 0 ? messageList : [''],
            senderDisplayName: parsedData.conversationEntry.senderDisplayName,
            timestamp: parsedData.conversationEntry.clientTimestamp,
            type: parsedData.conversationEntry.sender.role === 'EndUser' ? 'user' : 'agent',
            formatType: staticContent.formatType,
            attachments: staticContent.formatType === 'Attachments' ? staticContent.attachments : undefined,
            isMessageRead: false,
            showFeedback: false,
            urlMapping: [...(urlMapping ? [urlMapping] : [])],
          },
        ],
      }));

      // If the chat window is minimized, increment the unread messages count
      // This is to ensure that the unread messages count is only incremented when the chat window is minimized
      if (parsedData.conversationEntry.sender.role !== 'EndUser') {
        setChatState((prev) => {
          const newUnreadCount = prev.unreadMessages ? prev.unreadMessages + 1 : 1;
          const shouldPlayAudio = newUnreadCount > prevUnreadMessagesRef.current && newUnreadCount > 0 && (document.hidden || chatWindow === 'minimized');

          // Play audio only if unread messages are incrementing
          if (shouldPlayAudio) {
            playSoundMessageNotif?.();
          }

          prevUnreadMessagesRef.current = newUnreadCount;
          return {
            ...prev,
            unreadMessages: newUnreadCount,
          };
        });
      }
    },
    [setChatState, setState],
  );

  const handleRoutingEvent = (event: IMessageEvent) => {
    const { data } = event;
    const parsedData = JSON.parse(data) as IData;

    const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload) as IRoutingResultPayload;
    if (parsedPayload?.failureType === 'RoutingError' && parsedPayload?.failureReason && parsedPayload?.failureReason !== '') {
      logCustomAttributesNR('ca-chatbot-miaw-retrieve-chat', {
        logLevel: 'error',
        message: 'Agents are not available',
        conversationId: parsedData.conversationId,
        apiType: 'useMIAWAgentActions',
      });
      setState((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          {
            id: parsedData.conversationEntry.identifier,
            messageContent:
              parsedPayload?.failureReason ??
              'Sorry, our specialists aren’t available right now. Please try again later or contact us by email or phone instead.',
            timestamp: parsedData.conversationEntry.clientTimestamp,
            type: 'system',
            showFeedback: false,
          },
        ],
      }));
      setState((prev) => ({ ...prev, isRoutingError: true }));
    } else {
      handleResumeCTA();
      setState((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          {
            id: parsedData.conversationEntry.identifier,
            messageContent: `${enableSneakPeak ? "The specialist can see what you're typing to help you faster." : 'A specialist will be with you soon'}`,
            //   senderDisplayName: parsedData.conversationEntry.senderDisplayName,
            timestamp: parsedData.conversationEntry.clientTimestamp,
            hideTimestamp: enableSneakPeak,
            type: 'system',
            showFeedback: false,
          },
        ],
      }));
    }

    if (parsedPayload.routingType == 'Transfer') {
      localStorage.setItem('isCaseTransferring', 'true');
    }

    setChatbotState((prev) => ({
      ...prev,
      isSendMessageLoading: false,
    }));
  };

  const handleParticipantChangedEvent = (event: IMessageEvent) => {
    const { data } = event;
    const parsedData = JSON.parse(data) as IData;

    const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload) as IParticipantChangedPayload;

    if (parsedPayload?.entries?.[0]?.participant?.role === 'Supervisor') {
      return;
    }
    const sendRoutingDataWithMessage = parsedPayload.entries[0].operation === 'remove' ? true : false;

    if (sendRoutingDataWithMessage) setIsSendRoutingAttributes(true);
    if (!sendRoutingDataWithMessage) handleResumeCTA();

    setState((prev) => ({
      ...prev,
      conversationMessages: [
        ...prev.conversationMessages,
        {
          id: parsedData.conversationEntry.identifier,
          messageContent: getConversationEventMessage(parsedPayload.entries[0].operation, parsedPayload.entries[0].displayName),
          //   senderDisplayName: parsedData.conversationEntry.senderDisplayName,
          timestamp: parsedData.conversationEntry.clientTimestamp,
          type: 'system',
          showFeedback: false,
        },
      ],
      agentName: parsedPayload.entries?.[0]?.operation === 'add' ? parsedPayload.entries?.[0]?.displayName : '',
      sendRoutingDataWithMessage,
    }));

    if (parsedPayload.entries[0]?.participant?.role == 'Agent' && sendRoutingDataWithMessage && localStorage.getItem('isCaseTransferring') !== 'true') {
      setState((prev) => ({
        ...prev,
        conversationMessages: [
          ...prev.conversationMessages,
          {
            id: parsedData.conversationEntry.identifier,
            messageContent: <ResumeButton />,
            timestamp: parsedData.conversationEntry.clientTimestamp,
            type: 'Resume',
            showFeedback: false,
          },
        ],
        isChatEndedByAgent: true,
        agentName: parsedPayload.entries?.[0]?.operation === 'add' ? parsedPayload.entries?.[0]?.displayName : '',
        sendRoutingDataWithMessage,
      }));
    }

    if (parsedPayload.entries[0].operation === 'add' && localStorage.getItem('isCaseTransferring') === 'true') {
      localStorage.setItem('isCaseTransferring', 'false');
    }
  };

  const handleTypingIndicatorEvent = (event: IMessageEvent) => {
    const { data } = event;
    const parsedData = JSON.parse(data) as IData;
    const parsedPayload = JSON.parse(parsedData.conversationEntry.entryPayload) as ITypingIndicatorPayload;

    /** only for agent typing indicator */
    if (parsedData.conversationEntry.sender.role !== 'EndUser') {
      if (parsedPayload.entryType === 'TypingStartedIndicator') {
        setState((prev) => {
          if (prev.conversationMessages.some((message) => message.type === 'Typing')) {
            // If a typing indicator already exists, do not add a new one
            return prev;
          }
          return {
            ...prev,
            conversationMessages: [
              ...prev.conversationMessages,
              {
                id: parsedData.conversationEntry.identifier,
                messageContent: 'typing',
                senderDisplayName: parsedData.conversationEntry.senderDisplayName,
                timestamp: parsedData.conversationEntry.clientTimestamp,
                type: 'Typing',
                showFeedback: false,
              },
            ],
          };
        });
      } else if (parsedPayload.entryType === 'TypingStoppedIndicator') {
        setState((prev) => ({
          ...prev,
          conversationMessages: prev.conversationMessages.filter((message) => message.type !== 'Typing'),
        }));
      }
    }
  };

  const subscribeToAgentActions = useCallback(() => {
    try {
      console.info('[Chat-Anywhere] SF connection established.');
      addEventListeners();
    } catch (error) {
      logCustomAttributesNR('ca-chatbot-miaw-error', {
        errorName: error.name,
        errorStack: error.stack,
        message: error.message,
        logLevel: 'error',
      });
    }
  }, []);
  return { subscribeToAgentActions, handleCloseEventSource };
};
